import React from "react";
import {
	ChildrenProps,
	Column,
	Heading,
	If,
	Modal,
	Paragraph,
	Row,
	Rule,
	Section,
	TruncatedText,
} from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import {
	ClassYearNameFragment,
	Student,
	StudentEnrollmentIntent,
	StudentNameAndImageFragment,
} from "../../../../../Utilities";
import { ClassYearFormat, StudentFormat } from "../../../../Format";
import { StudentEnrollmentIntentField, StudentEnrollmentIntentForm } from "../../../../Forms";
import { StudentUtilities } from "../../../../Utilities";
import { StudentIcon } from "../../../Student/Icon";
import { BooleanBadge } from "../BooleanBadge";
import { ReEnrollmentMealPlanContent } from "../MealPlan";
import { tableColumns, tableColumnWidths, useStudentTuitionText } from "../utilities";
import generalStyles from "../styles.module.css";

export type ReEnrollmentDisplayProps = {
	students: (Pick<Student, "id" | "graduationYear"> &
		StudentNameAndImageFragment & {
			studentEnrollmentIntents: Pick<
				StudentEnrollmentIntent,
				"id" | "isEnrolling" | "isInterestedInMealPlan" | "declineReason" | "declineReasonDescription"
			>[];
		})[];
	classYears: Record<number, ClassYearNameFragment>;
	isWide: boolean;
};

export function ReEnrollmentDisplay({ students, classYears, isWide }: ReEnrollmentDisplayProps) {
	return (
		<Modal.Body className={generalStyles["modal__body"]}>
			<Section>
				<Section.Header>
					<Heading.H3 noMargin>Re-Enroll Students</Heading.H3>
				</Section.Header>
				<Section.Body>
					<Paragraph className={generalStyles["intro-paragraph"]}>
						Please specify the students you would like to re-enroll for the 2024-2025 school year. Tuition won't be due
						until later.
					</Paragraph>

					<Column justify="spaced-start" verticalSpacing="0.5rem">
						<div className={isWide ? generalStyles["field-area-container"] : undefined} style={{ paddingBottom: 0 }}>
							<ReEnrollmentStudents isWide={isWide}>
								{students.map((student, i) => {
									const isLast = i === students.length - 1;

									return (
										<ReEnrollmentStudent
											key={student.id}
											student={student}
											classYears={classYears}
											isLast={isLast}
											isWide={isWide}
										/>
									);
								})}
							</ReEnrollmentStudents>
						</div>

						<span className={generalStyles["notice"]}>
							<span className={generalStyles["notice__asterisk"]}>*</span>Price reflects tuition and any applicable
							discounts.
						</span>
					</Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading.H3 noMargin>Other Options</Heading.H3>
				</Section.Header>
				<Section.Body>
					<Column justify="spaced-start" verticalSpacing="0.5rem">
						<div className={isWide ? generalStyles["field-area-container"] : undefined}>
							<ReEnrollmentMealPlanContent isWide={isWide}>
								<BooleanBadge
									value={students.some((student) => student.studentEnrollmentIntents[0].isInterestedInMealPlan)}
									style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
								/>
							</ReEnrollmentMealPlanContent>
						</div>

						<span className={generalStyles["notice"]}>
							<span className={generalStyles["notice__asterisk"]}>*</span>You'll have the chance to select meal plans
							for each student later.
						</span>
					</Column>
				</Section.Body>
			</Section>
		</Modal.Body>
	);
}

type ReEnrollmentStudentsProps = {
	isWide: boolean;
} & Partial<ChildrenProps>;

function ReEnrollmentStudents({ isWide, children }: ReEnrollmentStudentsProps) {
	if (isWide) {
		return (
			<Table columns={tableColumns} columnWidths={tableColumnWidths}>
				<Table.Header>
					<Table.Row className={generalStyles["students-table__header__row"]}>
						<Table.Heading column="icon" noResize></Table.Heading>
						<Table.Heading column="studentName" noResize>
							Student
						</Table.Heading>
						<Table.Heading column="currentYear" noResize>
							Current Year
						</Table.Heading>
						<Table.Heading column="nextYear" noResize>
							Next Year
						</Table.Heading>
						<Table.Heading column="tuition" noResize>
							Tuition
						</Table.Heading>
						<Table.Heading column="isEnrolling" noResize style={{ textAlign: "right" }}>
							Re-Enroll?
						</Table.Heading>
					</Table.Row>
				</Table.Header>
				<Table.Body>{children}</Table.Body>
			</Table>
		);
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1rem">
			{children}
		</Column>
	);
}

type ReEnrollmentStudentProps = {
	student: ReEnrollmentDisplayProps["students"][0];
	classYears: Record<number, ClassYearNameFragment>;
	isLast: boolean;
	isWide: boolean;
};

function ReEnrollmentStudent({ student, classYears, isLast, isWide }: ReEnrollmentStudentProps) {
	const studentEnrollmentIntent = student.studentEnrollmentIntents[0];
	const { formState } = StudentEnrollmentIntentForm.useReadOnlyForm({
		studentEnrollmentIntent,
		fields: {
			declineReason: true,
			declineReasonDescription: true,
		},
	});

	const classYear = StudentUtilities.getClassYear(student, classYears);
	const nextClassYear = StudentUtilities.getNextClassYear(student, classYears);

	const tuitionText = useStudentTuitionText(student.id);

	if (isWide) {
		return (
			<React.Fragment>
				<Table.Row
					className={generalStyles["students-table__body__row"]}
					style={{ "--table__cell---padding": "1rem 0.5rem", lineHeight: "1.75" } as React.CSSProperties}
				>
					<Table.Cell column="icon" style={{ paddingTop: 0, paddingBottom: 0 }}>
						<Row justify="center" align="center" style={{ height: "100%" }}>
							<StudentIcon student={student} size={32} />
						</Row>
					</Table.Cell>
					<Table.Cell column="studentName" oneLine>
						{StudentFormat.name(student)}
					</Table.Cell>
					<Table.Cell column="currentYear" oneLine>
						{classYear ? ClassYearFormat.name(classYear) : "—"}
					</Table.Cell>
					<Table.Cell column="nextYear" oneLine>
						{nextClassYear ? ClassYearFormat.name(nextClassYear) : "—"}
					</Table.Cell>
					<Table.Cell column="tuition" oneLine style={{ fontWeight: "bold" }}>
						{tuitionText}
					</Table.Cell>
					<Table.Cell column="isEnrolling" style={{ paddingTop: 0, paddingBottom: 0 }}>
						<Row justify="flex-end" align="center" style={{ height: "100%" }}>
							<BooleanBadge value={studentEnrollmentIntent.isEnrolling} />
						</Row>
					</Table.Cell>
				</Table.Row>
				<If condition={!studentEnrollmentIntent.isEnrolling}>
					<div className={generalStyles["decline-reasons"]} style={{ marginBottom: "1rem" }}>
						<StudentEnrollmentIntentField.DeclineReasonDescription
							formState={formState}
							label="Reason for Leaving"
							style={{ width: "100%" }}
						/>
					</div>
				</If>
				<If condition={!isLast}>
					<Rule />
				</If>
			</React.Fragment>
		);
	}

	return (
		<div className={generalStyles["full-tile"]}>
			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.5rem"
				className={generalStyles["student-table__header"]}
			>
				<StudentIcon student={student} size={40} />
				<TruncatedText className={generalStyles["student-table__name"]}>{StudentFormat.name(student)}</TruncatedText>
			</Row>

			<Column className={generalStyles["student-table__body"]}>
				<Row justify="space-between" className={generalStyles["student-table__row"]}>
					<span className={generalStyles["student-table__cell--label"]}>Current Year:</span>
					<span className={generalStyles["student-table__cell--value"]}>
						{classYear ? ClassYearFormat.name(classYear) : "—"}
					</span>
				</Row>
				<Row justify="space-between" className={generalStyles["student-table__row"]}>
					<span className={generalStyles["student-table__cell--label"]}>Next Year:</span>
					<span className={generalStyles["student-table__cell--value"]}>
						{nextClassYear ? ClassYearFormat.name(nextClassYear) : "—"}
					</span>
				</Row>
				<Row justify="space-between" className={generalStyles["student-table__row"]}>
					<span className={generalStyles["student-table__cell--label"]}>Tuition:</span>
					<span className={generalStyles["student-table__cell--value"]}>{tuitionText}</span>
				</Row>
				<Row
					justify="space-between"
					className={generalStyles["student-table__row"] + " " + generalStyles["student-table__row--is-enrolling"]}
				>
					<span className={generalStyles["student-table__cell--label"]}>Re-Enroll?</span>
					<span className={generalStyles["student-table__cell--value"]}>
						<BooleanBadge
							value={studentEnrollmentIntent.isEnrolling}
							style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
						/>
					</span>
				</Row>
				<If condition={!studentEnrollmentIntent.isEnrolling}>
					<div className={generalStyles["decline-reasons"]} style={{ marginBottom: "1rem" }}>
						<StudentEnrollmentIntentField.DeclineReasonDescription
							formState={formState}
							label="Reason for Leaving"
							style={{ width: "100%" }}
						/>
					</div>
				</If>
			</Column>
		</div>
	);
}
